<template>
  <div class="w-screen h-screen flex">
    <!-- Left Section -->
    <div class="left-section w-1/2 flex flex-col items-center justify-center p-10">
      <figure class="logo-container mb-8">
        <img src="@/assets/main-logo.png" alt="Logo" class="h-20" />
      </figure>
    </div>

    <!-- Right Section -->
    <div class="right-section w-1/2 bg-white flex items-center justify-center">
      <div class="bg-white shadow-lg rounded-xl p-8 max-w-md w-full border border-gray-200">
        <!-- Title -->
        <div class="flex flex-col items-center mb-8">
          <div class="bg-orange-500 bg-opacity-20 p-3 rounded-full">
            <i class="fas fa-envelope text-3xl" style="color: #00aeef !important;"></i>
          </div>
          <h2 class="text-xl font-bold text-gray-800 mb-3">Verify Your Email Address</h2>
          <p class="text-gray-500 text-center">
            Enter the verification code sent to your email address <span class="font-semibold text-black">'{{ email }}'</span> to proceed.
          </p>
        </div>

        <!-- OTP Input -->
        <div class="grid grid-cols-6 gap-3 mb-10">
          <input
            v-for="(digit, index) in 6"
            :key="index"
            :ref="`otpInput${index}`"
            v-model="otpDigits[index]"
            @input="onInput(index)"
            @keydown="onKeyDown($event, index)"
            @paste="handlePaste"
            class="custom-focus w-full h-14 text-center text-2xl font-semibold rounded-lg bg-gray-100 border-1 border-gray-300 transition-colors"
            type="text"
            inputmode="numeric"
            maxlength="1"
            :id="`otp-${index}`"
          />
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message flex items-center justify-between p-3 mb-4">
          <div class="flex items-center">
            <i class="fas fa-exclamation-circle text-red-600 mr-2"></i>
            <span class="text-red-600 font-medium">{{ error }}</span>
          </div>
          <button @click="error = ''" class="text-red-600 hover:text-red-800">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Verify Button -->
        <button
          @click="verifyOtp"
          :disabled="!isOtpComplete || isLoading"
          class="custom-button w-full flex items-center justify-center"
        >
          <span v-if="!isLoading">Verify Email</span>
          <span v-else class="auth-loader"></span>
        </button>

        <!-- Resend Code -->
        <div class="mt-4 text-sm text-center">
          OTP not received?
          <button
            @click="resendOtp"
            :disabled="resendTimer > 0"
            class="register-text"
          >
            {{ resendTimer > 0 ? `Resend Code (${resendTimer}s)` : 'Resend Code' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import * as authService from "../service/auth";

export default {
name: "EmailVerification",
data() {
  return {
    otpDigits: ["", "", "", "", "", ""],
    password: localStorage.getItem("pendingPassword") || "",
    currentIndex: 0,
    resendTimer: 0,
    resendInterval: null,
    error: "", // Error message state
    isLoading: false,
  };
},
computed: {
  email() {
    return this.$route.query.email || "";
  },
  isOtpComplete() {
    return this.otpDigits.every((digit) => digit !== "");
  },
  otpCode() {
    return this.otpDigits.join("");
  },
},
watch: {
  otpDigits: {
    deep: true,
    handler(newVal) {
      if (newVal.join("").length == 6) {
        this.verifyOtp();
      }
    },
  },
},
mounted() {
  this.$nextTick(() => {
    if (this.$refs[`otpInput0`] && this.$refs[`otpInput0`][0]) {
      this.$refs[`otpInput0`][0].focus(); // Access the first input element
    }
  });
},
methods: {
  onInput(index) {
    this.otpDigits[index] = this.otpDigits[index].replace(/[^0-9]/g, "");
    if (this.otpDigits[index] && index < 5) {
      this.$refs[`otpInput${index + 1}`][0].focus();
    }
  },
  onKeyDown(event, index) {
    if (event.key === "Backspace" && index > 0 && !this.otpDigits[index]) {
      this.$refs[`otpInput${index - 1}`][0].focus();
    }
  },
  handlePaste(event) {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text").slice(0, 6);
    pastedData.split("").forEach((char, i) => {
      this.otpDigits[i] = char;
    });
  },
  async verifyOtp() {
    this.error = ""; // Reset error
    this.isLoading = true;
    try {
      await authService.confirmUserApi(this.email, this.otpDigits.join(""));

      // Automatically log the user in after successful verification
      await authService.loginApi(this.email, this.password);

      // Clear the password from localStorage
      localStorage.removeItem("pendingPassword");

      // Redirect to the appropriate page after login
      this.$router.push("/site-list");
      this.isLoading = false;
    } catch (error) {
      this.error = error.message || "OTP verification failed. Please try again.";
      this.isLoading = false;
    }
  },

  async resendOtp() {
    if (this.resendTimer > 0) return;

    this.error = ""; // Reset error
    try {
      const response = await authService.resendConfirmationCode(this.email);
      console.log("Resend Code Response:", response);
      if (response.success) {
        this.startResendTimer();
      } else {
        this.error = `Failed to resend confirmation code: ${response.error}`;
      }
    } catch (error) {
      console.error("Error resending confirmation code:", error.message);
      this.error = error.message || "Error occurred while resending the code.";
    }
  },
  startResendTimer() {
    this.resendTimer = 30;
    this.resendInterval = setInterval(() => {
      if (this.resendTimer > 0) {
        this.resendTimer--;
      } else {
        clearInterval(this.resendInterval);
      }
    }, 1000);
  },
},
beforeUnmount() {
  if (this.resendInterval) {
    clearInterval(this.resendInterval);
  }
},
};
</script>
